import React, { useState } from "react";
import cx from "classnames";
import { Redirect } from 'react-router-dom';

import CmsContentList from 'components/data/CmsContentList.js';
import ArchiveToken from "components/account/card/token-actions/ArchiveToken.js";
import { getPlaceholderKey } from 'components/account/card/activate-card/ActivateCard.js';
import CmsContentRenderer from 'components/data/CmsContentRenderer.js';
import CmsContentRendered from 'components/data/CmsContentRendered.js';
import { getPathByRoute } from "App.js";
import routeKeys from 'CustomerRouteKeys.js';
import LoadingIcon, { SIZES } from 'components/icons/LoadingIcon.js';
import { useGlobalToastsContext } from 'context/ToastProvider';
import { Last4Digits } from 'components/account/card/TokenNameAndPan.js';
import { centsToDisplay } from 'utils/FormatHelpers.js';
import { TOKEN } from "utils/Constants.js";
import { TRAVEL_TOKEN_TYPES, openTransitTemporary, openTransitVirtual, virtualCard , openTransitRegular, openTransitRestricted } from "server/api-types/WSSubsystemAccountTravelTokenDisplayFactory.js";
import { useModalContext } from "context/ModalProvider.js";
import PublicAppVars from "utils/PublicAppVars.js";

import CardLayout, { getCardLayoutBreadcrumbs } from 'layouts/CardLayout.js';
import Button, { buttonTypeStylePlain } from "components/Button.js";
import TokenStatus from 'components/account/card/TokenStatus.js';
import TokenExpiryDate from 'components/account/card/TokenExpiryDate.js';
import { CardImage } from 'components/icons/CardBrands.js';
import EditCardNickname from "components/modals/EditCardNickname.js";
import MyGroups from 'components/account/panels/MyGroups.js';
import ResumeUseCard from 'components/modals/ResumeUseCard.js';
import ErrorAutoload from "components/modals/ErrorAutoload";

import CmsContentRenderedInline from "components/data/CmsContentRenderedInline";
import { useBreadcrumbCallback } from "context/BreadcrumbProvider";
import {
	isGhostCard,
	findPrimaryToken,
} from "components/manage-cards/TokenHelpers.js";
import { OPEN_TRANSIT_RESTRICTED_CARD, OPEN_TRANSIT_TEMPORARY_CARD } from "server/api-types/WSSubsystemAccountToken.js";
import { BYPASS_TO_REPLACEMENT } from "pages/account/card/BlockOrReplaceFlow.js";
import { STEP_OFFSET_PARAM } from "context/StepContext.js";
import {
	ACTION_TYPE_PARAM,
	REASON_TYPE_PARAM,
	RESOLUTION_TYPE_PARAM,
	replaceReasonMapping,
} from "pages/account/card/replacement/ReasonOptions.js";
import {
	CARD_ACTIONS,
	CARD_RESOLUTIONS,
	CARD_REASONS,
} from "pages/account/card/replacement/constants.js";
import Toast, { TYPE_ERROR } from "components/Toast.js";
import FormHelper from "utils/FormHelper.js";
import TerminateToken from "components/account/card/token-actions/TerminateToken.js";
import { useTransitAccountIdContext } from "context/TransitAccountIdContext.js";
import { useTransitAccount } from 'components/data/transit-account/TransitAccount.js';
import { useIsEmvCard } from "components/data/transit-account/EMV.helpers.js";
import PrimaryToken from "components/account/card/token-actions/PrimaryToken.js";
import { CARD_STATUS_SUSPENDED, getCardType } from "utils/Cards.js";
import {
	replaceTransitAccountMedia,
	upgradeTransitAccountMedia,
} from 'pages/account/card/BlockOrReplaceSelect.js';
import { useReplacementOptions } from "components/data/transit-account/ReplacementOptions.query";

import * as panel from "components/Panel.module.css";
import * as settings from "./Settings.module.css";
import * as admin from 'layouts/Admin.module.css';
import * as typography from "styles/typography.module.css";
import { TOKEN_ENABLEMENT_FEE_STATUS } from "server/api-types/WSTravelTokenEnablementFeeInfo";
import PurseBalanceProvider, { usePurseBalanceContext } from "context/PurseBalanceContext";
import Modal from "components/Modal";


const cms = {
	header: "miscText.card-settings-header",
	description: "miscText.card-settings-description",
	typeStandard: "miscText.card-settings-type-standard",
	serialNumber: "miscText.card-settings-serialnumber",
	status: "miscText.card-settings-status",
	expiry: "miscText.card-settings-expiry",
	enablement: "miscText.card-settings-enablement",
	freeze: "miscText.card-settings-freeze-replace-cta",
	resume: "miscText.card-settings-unfreeze-cta",
	replace: "miscText.card-settings-replace-cta",
	terminate: "miscText.card-settings-terminate-cta",
	manageHeader: "miscText.card-settings-manage-subheader",
	nicknameCta: "miscText.card-settings-manage-nickname-cta",
	unlinkCta: "miscText.card-settings-manage-unlink-transit-cta",
	unlinkErrorHeader: "miscText.card-settings-manage-unlink-error-header",
	unlinkErrorAutoload: "miscHtml.card-settings-manage-unlink-error-autoload",
	unlinkErrorSubmit: "miscText.card-settings-manage-unlink-error-submit",
	upgradeBtn: "miscText.card-settings-upgrade-cta",

	delinkMobileCardHeader: "miscText.card-settings-manage-unlink-error-header",
	delinkMobileCardText: "miscHtml.card-settings-manage-unlink-error-mobile",

	blockCard: 'miscText.card-freeze-header-block',

	unlinkErrorHeader: "miscText.card-settings-manage-unlink-error-header",
	unlinkErrorDesc: "miscText.card-settings-manage-unlink-error-description",
	unlinkErrorBtn: "miscText.card-settings-manage-unlink-error-btn",
};


const ReplaceTravelToken = 'ReplaceTravelToken';

const getTokenActions = ({
	tokenInfo,
	primaryToken,
	mediaProducts,
	tokenId,
	isEmv,
	mediaType,
	statusReasonCode,
	status,
	frontendStatus,
	filteredTokens,
	canBePrimary = false,
	riderClass,
	restrictActions,
	replaceOrderInfo,
	upgradeMediaOptions,
	isTempCard,
	isHandsFreeCard,
}) => {

	const isPrimaryToken = tokenId === primaryToken.tokenId;

	// Tokens closed with the following reason codes can't be
	// terminated, replaced or made primary
	// statusReasonCode should already be a string, but just in case we'll convert it.
	const isInActionableToken = PublicAppVars.INACTIONABLE_TOKEN_REASON_CODES.map(object => object.reasonCodeId).includes(statusReasonCode.toString());
	const isMobileFareCard = PublicAppVars.MOBILE_FARE_CARD_TOKEN_MEDIA_TYPES.includes(mediaType);
	const canUnblock = status === TOKEN.STATUS.closed && isInActionableToken;

	const elidgibleForReplacement = (!replaceOrderInfo || replaceOrderInfo?.length === 0)
		&& (!restrictActions || !restrictActions.includes(pair => pair.key === ReplaceTravelToken));

	// Can not be EMV
	// Can not be a temporary card
	// Can not be a handsfree card
	// frontendStatus must be expired OR it can be unblocked
	// statusReasonCode cannot be Lost or Stolen
	// can not be a mobile fare card
	const canReplace = !isEmv
		&& !isTempCard
		&& !isHandsFreeCard
		&& status === TOKEN.STATUS.closed
		// replacement options were not returned for expired Agency Card
		// https://reflexions.slack.com/archives/CCF68M49M/p1759950730290909?thread_ts=1759940034.879789&cid=CCF68M49M
		&& mediaProducts.length > 0
		&& !isInActionableToken
		&& !isMobileFareCard
		&& elidgibleForReplacement;

	// Must be a temp card
	// Can not have previous orders (replaceOrderInfo)
	// Can not be replace restricted (restrictActions)
	// must have upgrade options available
	const canUpgrade = isTempCard
		&& PublicAppVars.ENABLE_UPGRADE_CARD
		&& elidgibleForReplacement
		&& upgradeMediaOptions?.length > 0;

	//https://reflexions.atlassian.net/browse/MBTA-3126
	const isActionToTerminateOrRemove = mediaType === openTransitRegular
		|| mediaType === openTransitRestricted
		|| mediaType === openTransitTemporary
		|| mediaType === openTransitVirtual
		|| mediaType === virtualCard;

	const canTerminate = canUnblock
		&& !isInActionableToken
		&& !isMobileFareCard
		&& isActionToTerminateOrRemove;

	const canBlock = tokenInfo.tokenType === TRAVEL_TOKEN_TYPES.smartcard
		&& frontendStatus === TOKEN.STATUS.active
		&& (tokenInfo.tokenEnablementFee.status === TOKEN_ENABLEMENT_FEE_STATUS.PAID || tokenInfo.tokenEnablementFee.status === TOKEN_ENABLEMENT_FEE_STATUS.WAIVED);

	// https://reflexions.atlassian.net/browse/MBTA-3142
	// Any token can be made primary if it meets the following criteria:
	// 1. Closed
	// 2. canBePrimary = true
	// 3. Has an enabled rider class
	// 4. Does not have a disabled reason code
	const canMakePrimary = status === TOKEN.STATUS.closed
	&& canBePrimary
	&& PublicAppVars.PRIMARY_ENABLED_RIDER_CLASSES.includes(riderClass.toString()) // BO is really inconsistent with Strings vs Int for their ID's we will always assume it is a String
	&& !PublicAppVars.DISABLED_TOKEN_RESONCODE_IDS.includes(statusReasonCode.toString());

	// To remove a token it must be:
	// 1. Terminated or Lost or Stolen
	// 2. Not the Primary Token
	// 3. There must be more than 1 token associated with the transitAccount
	const canArchiveToken = (isInActionableToken || (frontendStatus === TOKEN.STATUS.terminated))
	&& isActionToTerminateOrRemove
	&& !isPrimaryToken
	&& filteredTokens.length > 1;

	return {
		canUpgrade,
		canBlock,
		canUnblock,
		canReplace,
		canTerminate,
		canMakePrimary,
		canArchiveToken,
	};
};

// Card style by default has no border radius.
// The logic here determines where to apply border radius so all cards are contiguous with only top and bottom cards having a radius.
const getCardStyles = ({ isActive, isFirst, isLast }) =>
	cx(panel.card,
		isActive
			? null
			: panel.isNotActive,
		(isFirst && isLast)
			? panel.isOneCard
			: isFirst
				? panel.isFirst
				: isLast
					? panel.isLast
					: null,
	);

const TokenActions = ({
	filteredTokens,
	wsTransitAccountToken,
	onModalClose,
	riderClass,
}) => {
	const subsystemAccountReference = useTransitAccountIdContext();
	const { setModal } = useModalContext();
	const [ error, setError ] = useState();

	const isEmv = useIsEmvCard({ subsystemAccountReference });

	const {
		tokenInfo,
		mediaType,
		tokenId,
		frontendStatus,
		status,
		statusReasonCode,
		canBePrimary,
		restrictActions,
		replaceOrderInfo,
		upgradeMediaOptions,
	} = wsTransitAccountToken;

	const primaryToken = findPrimaryToken(filteredTokens);
	const replacementType = primaryToken?.mediaType === OPEN_TRANSIT_TEMPORARY_CARD
		? upgradeTransitAccountMedia
		: replaceTransitAccountMedia;

	const {
		mediaProducts,
		queryResult,
	} = useReplacementOptions({ tokenId: primaryToken?.tokenId, replacementType, subsystemAccountReference });

	if (queryResult.loading) {
		return <LoadingIcon size={SIZES.page} />;
	}

	// https://reflexions.atlassian.net/browse/MBTA-2615
	// statusReasonCode should be an ID not a string - "Token Expired"
	// implement a workaround to handle the current reason
	const mappedStatusReasonCode = statusReasonCode === "Token Expired"
		? replaceReasonMapping[ CARD_REASONS.expired ].reasonCode
		: statusReasonCode;

	const isTempCard = mediaType === OPEN_TRANSIT_TEMPORARY_CARD;
	const isHandsFreeCard = mediaType === OPEN_TRANSIT_RESTRICTED_CARD;

	const argObj = {
		riderClass,
		tokenId,
		isEmv,
		mediaType,
		statusReasonCode,
		status,
		frontendStatus,
		primaryToken,
		mediaProducts,
		filteredTokens,
		canBePrimary,
		restrictActions,
		replaceOrderInfo,
		upgradeMediaOptions,
		tokenInfo,
		isTempCard,
		isHandsFreeCard,
	};

	const {
		canUpgrade,
		canBlock,
		canUnblock,
		canReplace,
		canTerminate,
		canMakePrimary,
		canArchiveToken,
	} = getTokenActions(argObj);

	const replaceRedirect = getPathByRoute(routeKeys.AccountCardBlockOrReplace, {
		transit_account_id: subsystemAccountReference,
		replacedTravelTokenId: tokenId,
	}) + `?${STEP_OFFSET_PARAM}=${BYPASS_TO_REPLACEMENT}&${REASON_TYPE_PARAM}=${mappedStatusReasonCode}&${ACTION_TYPE_PARAM}=${CARD_ACTIONS.replaceCard}&${RESOLUTION_TYPE_PARAM}=${CARD_RESOLUTIONS.newCard}`;

	return <>
		{error && FormHelper.errorJsx(error)}

		{canUpgrade && <div>
			<Button
				typeStyle={buttonTypeStylePlain}
				additionalClassNames={settings.cardAction}
				to={getPathByRoute(routeKeys.AccountCardUpgrade, { transit_account_id: subsystemAccountReference })}
			>
				<CmsContentRenderer.Span
					contentKey={cms.upgradeBtn}
					fallbackValue="Upgrade your Charlie Card"
				/>
			</Button>
		</div>}

		{canBlock && <div>
			<Button
				typeStyle={buttonTypeStylePlain}
				to={getPathByRoute(routeKeys.AccountCardBlockOrReplace, {
					transit_account_id: subsystemAccountReference,
					replacedTravelTokenId: tokenId,
				})}
				additionalClassNames={settings.cardAction}
			>
				<CmsContentRenderer.Span
					fallbackValue={isTempCard || isHandsFreeCard ? "Block Card" : "Block or replace card"}
					contentKey={isTempCard || isHandsFreeCard ? cms.blockCard : cms.freeze}
				/>
			</Button>
		</div>}

		{canUnblock && <div>
			<Button
				typeStyle={buttonTypeStylePlain}
				onClick={() =>
					setModal(<ResumeUseCard {...{
						hasEnablementFee: (tokenInfo.tokenEnablementFee.feeDueAmount ?? 0) > 0,
						subsystemAccountReference,
						replaceRedirect,
						onModalClose,
						tokenId,
					}} />)
				}
				additionalClassNames={settings.cardAction}
			>
				<CmsContentRenderer.Span
					fallbackValue="Unblock card"
					contentKey={cms.resume}
				/>
			</Button>
		</div>}

		{canReplace && <div>
			<Button
				typeStyle={buttonTypeStylePlain}
				additionalClassNames={settings.cardAction}
				to={replaceRedirect}
			>
				<CmsContentRenderer.Span
					fallbackValue="Replace Card"
					contentKey={cms.replace}
				/>
			</Button>
		</div>}

		{canTerminate && <TerminateToken {...{ wsTransitAccountToken, setError }} />}
		{canArchiveToken && <ArchiveToken {...{ wsTransitAccountToken, setError }} />}
		{canMakePrimary && <PrimaryToken {...{ wsTransitAccountToken }} />}
	</>;
};

const Token = ({
	filteredTokens,
	onModalClose,
	wsTransitAccountToken,
	subsystemAccountReference,
	isFirst = true,
	isLast = true,
	riderClass,
}) => {
	const {
		frontendStatus,
		statusReasonCode,
		statusReasonCodeDesc,
		tokenInfo,
		mediaType,
	} = wsTransitAccountToken;

	const isEmv = useIsEmvCard({ subsystemAccountReference });
	const isActive = frontendStatus === TOKEN.STATUS.active;

	const cardStyles = getCardStyles({ isActive, isFirst, isLast });

	const cardType = getCardType({
		tokenInfo,
		isMonochrome: frontendStatus === CARD_STATUS_SUSPENDED,
		mediaType,
	});

	return (
		<CmsContentList list={Object.values(cms)}>{() =>
			<div className={cardStyles}>
				<CmsContentRendered.H2
					fallbackValue="Standard Fare Card"
					contentKey={getPlaceholderKey(tokenInfo.tokenType, mediaType)}
					variables={{ cardtype: tokenInfo.cardType }}
					className={cx(typography.h8, panel.cardName)}
				/>
				<div className={settings.cardDetails}>
					<div>
						<CardImage
							creditCardType={cardType}
							className={settings.cardImage}
							svgAriaDesc={tokenInfo.creditCardType}
							mobileWalletType={tokenInfo?.mobileWalletType}
							status={frontendStatus}
						/>
					</div>
					<div className={settings.cardDetailsContainer}>
						<div className={settings.cardInfo}>
							<CmsContentRenderer.Span
								className={settings.cardInfoLabel}
								contentKey={cms.serialNumber}
								fallbackValue="Serial Number"
							/>
							<span className={settings.cardInfoValue}>
								...<Last4Digits {...{ tokenInfo: wsTransitAccountToken.tokenInfo }} />
							</span>
						</div>
						<div className={settings.cardInfo}>
							<CmsContentRenderer.Span
								className={settings.cardInfoLabel}
								contentKey={cms.expiry}
								fallbackValue="Expires"
							/>
							<span className={settings.cardInfoValue}>
								<TokenExpiryDate isEmv={isEmv} tokenInfo={tokenInfo} />
							</span>
						</div>
						<div className={settings.cardInfo}>
							<CmsContentRenderer.Span
								className={settings.cardInfoLabel}
								contentKey={cms.status}
								fallbackValue="Status"
							/>
							<span className={settings.cardInfoValue}>
								<TokenStatus
									{...{ frontendStatus }}
									statusReasonCode={statusReasonCode}
									statusDescription={frontendStatus}
									statusReasonCodeDesc={statusReasonCodeDesc}
								/>
							</span>
						</div>
						{/* Only show if there is a fee attached */}
						{tokenInfo.tokenEnablementFee.feeDueAmount > 0 && (
							<div className={settings.cardInfo}>
								<CmsContentRenderer.Span
									className={settings.cardInfoLabel}
									fallbackValue="Enablement fee"
									contentKey={cms.enablement}
								/>
								<span className={settings.cardInfoValue}>
									{centsToDisplay(tokenInfo.tokenEnablementFee.feeDueAmount)}
								</span>
							</div>
						)}
					</div>
				</div>
				<TokenActions
					riderClass={riderClass}
					wsTransitAccountToken={wsTransitAccountToken}
					filteredTokens={filteredTokens}
					onModalClose={onModalClose}
				/>
			</div>
		}</CmsContentList>
	);
};


export const getCardSettingsBreadcrumbs = () => [
	...getCardLayoutBreadcrumbs(),
	<CmsContentRenderedInline
		key={cms.header}
		contentKey={cms.header}
		fallbackValue="Card Settings"
	/>,
];

const SettingsPage = () => {
	useBreadcrumbCallback(getCardSettingsBreadcrumbs);
	const subsystemAccountReference = useTransitAccountIdContext();
	const { hasNegativeBalance } = usePurseBalanceContext();

	const { setToast, removeToast } = useGlobalToastsContext();
	const { setModal } = useModalContext();
	const [ redirect, setRedirect ] = useState(null);
	const onModalClose = () => setModal(null);

	const handleUnlinkCard = ({ isMobileFareCard, hasAutoloads, cmsContent }) => {
		// https://reflexions.atlassian.net/browse/MBTA-2446
		// MobileFare/OpenTransitVirtual cards can not be delinked. Instead we throw a Toast message.
		if (isMobileFareCard) {
			setToast(<Toast
				type={TYPE_ERROR}
				title={<CmsContentRenderer.Span
					cmsContent={cmsContent}
					contentKey={cms.delinkMobileCardHeader}
					fallbackValue={'You may not remove this card'}
				/>}
				text={
					<CmsContentRenderer.Span
						cmsContent={cmsContent}
						contentKey={cms.delinkMobileCardText}
						fallbackValue={'<p>To unlink virtual cards, you must contact customer service.</p>'}
					/>
				}
				onClosed={removeToast}
			/>);

			return;
		}

		if (hasNegativeBalance) {
			setModal(
				<Modal
					title={<CmsContentRenderer.Span
						cmsContent={cmsContent}
						contentKey={cms.unlinkErrorHeader}
						fallbackValue={'You May Not Remove This Card'}
					/>}
				>
					<CmsContentRenderer.Span
						cmsContent={cmsContent}
						contentKey={cms.unlinkErrorDesc}
						fallbackValue={'A card with a negative balance cannot be removed.'}
					/>
					<div className={settings.unlinkErrorBtn}>
						<Button
							isPrimary
							type="submit"
							onClick={() => setModal(null)}
						>
							<CmsContentRenderer.Span
								cmsContent={cmsContent}
								contentKey={cms.unlinkErrorBtn}
								fallbackValue="I Understand"
							/>
						</Button>
					</div>
				</Modal>
			);
			return;
		}

		hasAutoloads
			? setModal(<ErrorAutoload onModalClose={onModalClose} cmsContent={cmsContent} />)
			: setRedirect(<Redirect push to={{ pathname: getPathByRoute(routeKeys.DeLinkCard, { transit_account_id: subsystemAccountReference }) }} />);
	};

	const {
		data: transitAccountQData,
		loading: transitAccountQLoading,
	} = useTransitAccount({ subsystemAccountReference, returnSubscriptions: true, returnTravelTokenActions: true, returnOrderInfo: true });

	if (transitAccountQLoading) {
		return <LoadingIcon size={SIZES.page} />;
	}

	if (redirect) {
		return redirect;
	}

	const {
		transitAccountQ: {
			tokens,
			subscriptions,
			riderClass,
		},
	} = transitAccountQData;

	const ghostCard = isGhostCard(transitAccountQData.transitAccountQ);
	const hasAutoloads = Boolean(subscriptions?.length);

	// Filter out archived tokens
	const filteredTokens = tokens.filter(token =>
		token.frontendStatus !== TOKEN.STATUS.archive
		&& token.statusReasonCodeDesc !== TOKEN.STATUS.archive
		&& token.frontendStatus !== TOKEN.STATUS.archived
		&& token.statusReasonCodeDesc !== TOKEN.STATUS.archived
	);

	const primaryToken = ghostCard
		? null
		: findPrimaryToken(filteredTokens);

	const isMobileFareCard = ghostCard
		? null
		// https://reflexions.atlassian.net/issues/MBTA-3313
		: primaryToken.tokenInfo.tokenType === TRAVEL_TOKEN_TYPES.openTransitVirtual || primaryToken.tokenInfo.tokenType === TRAVEL_TOKEN_TYPES.virtualCard;

	return (
		<CmsContentList list={Object.values(cms)}>{({ cmsContent }) =>
			<>
				<CardLayout title={<CmsContentRenderer contentKey={cms.header} cmsContent={cmsContent} fallbackValue="Charlie Card Settings" />}>
					<CmsContentRenderer.P
						cmsContent={cmsContent}
						className={settings.settingsTitle}
						contentKey={cms.description}
						fallbackValue="You can use your Charlie Card in different ways depending on what's most convenient for you."
					/>
					<div className={settings.container}>
						<div className={admin.pageCol}>
							<div className={settings.cardList}>
								{ghostCard
									? null
									: filteredTokens?.map((wsTransitAccountToken, index) =>
										<Token
											isFirst={index === 0}
											isLast={index + 1 === filteredTokens.length}
											onModalClose={onModalClose}
											key={wsTransitAccountToken.id}
											filteredTokens={filteredTokens}
											isPrimaryToken={wsTransitAccountToken.id === primaryToken.id}
											subsystemAccountReference={subsystemAccountReference}
											wsTransitAccountToken={wsTransitAccountToken}
											{...{ riderClass }}
										/>
									)}
							</div>
							{ghostCard
								? null
								: <div className={settings.subContainer}>
									<CmsContentRenderer.H3
										cmsContent={cmsContent}
										contentKey={cms.manageHeader}
										fallbackValue='Manage Card'
										className={admin.subTitle}
									/>
									<Button
										typeStyle={buttonTypeStylePlain}
										additionalClassNames={settings.cardAction}
										onClick={() =>
											setModal(
												<EditCardNickname
													tokenNickname={primaryToken?.tokenInfo.tokenNickname}
													tokenId={primaryToken?.id}
													transitAccountId={subsystemAccountReference} />
											)}
									>
										<CmsContentRenderer.Span
											cmsContent={cmsContent}
											contentKey={cms.nicknameCta}
											fallbackValue='Edit Card Nickname'
										/>
									</Button>
								</div>}
							{!isMobileFareCard &&
							// Mobile Fare cards should be unlinked from the account
							// https://reflexions.atlassian.net/issues/MBTA-3242
								<div>
									<Button
										className={settings.dangerUnlink}
										onClick={() => handleUnlinkCard({
											hasAutoloads,
											cmsContent,
											isMobileFareCard,
										})}
									>
										<CmsContentRenderer.Span
											cmsContent={cmsContent}
											fallbackValue='Unlink This Card From Your Account.'
											contentKey={cms.unlinkCta}
										/>
									</Button>
								</div>
							}
						</div>
						<div className={admin.pageCol}>
							{PublicAppVars.ENABLE_TOKEN_GROUPS ? <MyGroups /> : null}
						</div>
					</div>
				</CardLayout>
			</>
		}</CmsContentList>
	);
};

const Settings = () =>
	<PurseBalanceProvider>
		<SettingsPage />
	</PurseBalanceProvider>;

export default Settings;
